import { ReactNode } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { MoreOutlined } from '@ant-design/icons';

export interface ActionItem {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  /**
   * Menu-item style passthrough — the house Activate=green seat
   * (`{ color: token.colorSuccess }` on the inactive row's Activate item,
   * mirroring `danger` on Deactivate).
   */
  style?: React.CSSProperties;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

interface ActionDropdownProps {
  items: ActionItem[];
  trigger?: ('click' | 'hover')[];
}

type MenuItem = Required<MenuProps>['items'][number];

/**
 * Standardized three-dot action menu for table rows
 * @param items - Array of action menu items
 * @param trigger - Event trigger type (default: ['click'])
 */
const ActionDropdown = ({
  items,
  trigger = ['click'],
}: ActionDropdownProps) => {
  // Convert ActionItem[] to Ant Design MenuItem[]
  const menuItems: MenuItem[] = items.map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    danger: item.danger,
    style: item.style,
    onClick: () => {
      // Create a synthetic event to match ActionItem's onClick signature
      const syntheticEvent = new MouseEvent('click') as unknown as React.MouseEvent<HTMLElement>;
      item.onClick(syntheticEvent);
    },
  }));

  return (
    <Dropdown menu={{ items: menuItems }} trigger={trigger}>
      <Button
        type="text"
        icon={<MoreOutlined />}
        onClick={(e) => e.stopPropagation()} // Prevent row click
      />
    </Dropdown>
  );
};

/**
 * THE row-action column (Melissa 2026-08-28, app-wide ruling): a table's last
 * column carries **no header text** — an "Actions" title costs a full column
 * of header width to say what the `⋯` already says — and its cell is the
 * three-dot menu, with Edit and every other row action inside it.
 *
 * Use this factory rather than re-declaring the column per table, so the
 * width, the empty title, the right-pin and the menu control stay identical
 * everywhere:
 *
 * ```tsx
 * const columns = [ …, actionColumn<Schedule>((row) => [
 *   { key: 'edit', label: t('common:actions.edit'), onClick: () => openEdit(row) },
 * ])];
 * ```
 *
 * Row-level actions live HERE; a section- or card-level Edit is the separate
 * purple primary button (see `SectionHeader`). Two controls, two scopes.
 */
/* The factory sits beside the control it renders (TableContainer renderDash
   precedent) — a separate module would split one rule across two files. */
// eslint-disable-next-line react-refresh/only-export-components
export function actionColumn<T>(
  items: (row: T) => ActionItem[],
  options?: { width?: number }
): ColumnType<T> {
  return {
    title: '',
    key: 'actions',
    width: options?.width ?? 56,
    fixed: 'right',
    align: 'center',
    render: (_: unknown, row: T) => <ActionDropdown items={items(row)} />,
  };
}

export default ActionDropdown;
